import { ResponsiveRangePicker } from "../components/ResponsiveRangePicker";
import { SelectWithAll } from "../components/SelectWithAll";
import { SourcePicker } from "../components/SourcePicker";
import { useNotifications } from "../features/notifications";
import { arrayToDistinct } from "../helper/arrayToDistinct";
import { arrayToTree, treeToArray } from "../helper/arrayToTree";
import { catchError } from "../helper/catchError";
import * as ColorConvert from "../helper/ColorConvert";
import { equals } from "../helper/equals";
import { isType } from "../helper/isType";
import { useAppState } from "../hooks/useAppState";
import { useBreadcrumb } from "../hooks/useBreadcrumb";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useDebounceFunction } from "../hooks/useDebounceFunction";
import { useFeedback } from "../hooks/useFeedback";
import { useHref } from "../hooks/useHref";
import { useNavigation } from "../hooks/useNavigation";
import { useOpenDashApp } from "../hooks/useOpenDashApp";
import { useOpenDashServices } from "../hooks/useOpenDashServices";
import { usePath } from "../hooks/usePath";
import { usePermission } from "../hooks/usePermission";
import { usePermissions } from "../hooks/usePermissions";
import { useSource } from "../hooks/useSource";
import { useStorage } from "../hooks/useStorage";
import { useStorageService } from "../hooks/useStorageService";
import { useUrlParam } from "../hooks/useUrlParam";
import { useUserService } from "../hooks/useUserService";
import { useDebounce } from "../react-helper/useDebounce";
import { useDeepCompareCallback } from "../react-helper/useDeepCompareCallback";
import { useDeepCompareEffect } from "../react-helper/useDeepCompareEffect";
import { useDeepCompareMemo } from "../react-helper/useDeepCompareMemo";
import { useDeepCompareMemoize } from "../react-helper/useDeepCompareMemoize";
import { useElementSize } from "../react-helper/useElementSize";
import { useEventListener } from "../react-helper/useEventListener";
import { useForceRender } from "../react-helper/useForceRender";
import { useImmerState } from "../react-helper/useImmerState";
import { useInterval } from "../react-helper/useInterval";
import { useLocalStorage } from "../react-helper/useLocalStorage";
import { useObjectState } from "../react-helper/useObjectState";
import { usePromise } from "../react-helper/usePromise";
import { useServiceStore } from "../react-helper/useServiceStore";
import { useStore } from "../react-helper/useStore";

type ReactType = {
  Components: {
    Inputs: {
      SelectWithAll: typeof SelectWithAll;
      SourcePicker: typeof SourcePicker;
      ResponsiveRangePicker: typeof ResponsiveRangePicker;
    };
  };
  Hooks: {
    useFeedback: typeof useFeedback;
    useAppState: typeof useAppState;
    useCurrentUser: typeof useCurrentUser;
    useBreadcrumb: typeof useBreadcrumb;
    useHref: typeof useHref;
    useNavigation: typeof useNavigation;
    useOpenDashApp: typeof useOpenDashApp;
    useOpenDashServices: typeof useOpenDashServices;
    usePath: typeof usePath;
    usePermission: typeof usePermission;
    usePermissions: typeof usePermissions;
    useSource: typeof useSource;
    useStorage: typeof useStorage;
    useStorageService: typeof useStorageService;
    useUrlParam: typeof useUrlParam;
    useUserService: typeof useUserService;
    useDebounce: typeof useDebounce;
    useDeepCompareCallback: typeof useDeepCompareCallback;
    useDeepCompareEffect: typeof useDeepCompareEffect;
    useDeepCompareMemo: typeof useDeepCompareMemo;
    useDeepCompareMemoize: typeof useDeepCompareMemoize;
    useElementSize: typeof useElementSize;
    useEventListener: typeof useEventListener;
    useForceRender: typeof useForceRender;
    useImmerState: typeof useImmerState;
    useInterval: typeof useInterval;
    useLocalStorage: typeof useLocalStorage;
    useObjectState: typeof useObjectState;
    usePromise: typeof usePromise;
    useServiceStore: typeof useServiceStore;
    useStore: typeof useStore;
    useNotifications: typeof useNotifications;
    useDebounceFunction: typeof useDebounceFunction;
  };
};

export class Core {
  static React: ReactType = {
    Components: {
      Inputs: {
        SelectWithAll,
        SourcePicker,
        ResponsiveRangePicker,
      },
    },
    Hooks: {
      useFeedback,
      useAppState,
      useCurrentUser,
      useBreadcrumb,
      useHref,
      useNavigation,
      useOpenDashApp,
      useOpenDashServices,
      usePath,
      usePermission,
      usePermissions,
      useSource,
      useStorage,
      useStorageService,
      useUrlParam,
      useUserService,
      useDebounce,
      useDeepCompareCallback,
      useDeepCompareEffect,
      useDeepCompareMemo,
      useDeepCompareMemoize,
      useElementSize,
      useEventListener,
      useForceRender,
      useImmerState,
      useInterval,
      useLocalStorage,
      useObjectState,
      usePromise,
      useServiceStore,
      useStore,
      useNotifications,
      useDebounceFunction,
    },
  };
  static Helpers = {
    General: {
      isType,
      equals,
      catchError,
    },
    Array: {
      toDistinct: arrayToDistinct,
      toTree: arrayToTree,
      fromTree: treeToArray,
    },
    Colors: ColorConvert,
  };
}
